import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, CheckCircle2, Download, LogOut, PackageOpen, RefreshCw } from "lucide-react";
import { fetchOrders, fetchSentOrders, markOrdersAsSent, signOut } from "../lib/firebase";
import { downloadOrderCsv, getOrderTotal } from "../lib/orders";
import type { CurrentOrders, Feedback, SentOrder } from "../types";
import { FeedbackToast } from "./FeedbackToast";
import { BrandMark } from "./BrandMark";
import { OrderGroup } from "./OrderGroup";

const dateFormatter = new Intl.DateTimeFormat("sv-SE", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

export function AdminDashboard() {
  const [currentOrders, setCurrentOrders] = useState<CurrentOrders>({});
  const [sentOrders, setSentOrders] = useState<SentOrder[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const [current, sent] = await Promise.all([fetchOrders(), fetchSentOrders()]);
      setCurrentOrders(current);
      setSentOrders(sent);
      setSelected([]);
    } catch {
      setFeedback({ tone: "error", message: "Beställningarna kunde inte hämtas." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const totalItems = useMemo(
    () => Object.values(currentOrders).reduce((total, orders) => total + getOrderTotal(orders), 0),
    [currentOrders],
  );

  function toggleSelected(name: string, isSelected: boolean) {
    setSelected((items) => isSelected ? [...items, name] : items.filter((item) => item !== name));
  }

  async function handleMarkAsSent() {
    if (selected.length === 0) return;
    const confirmed = window.confirm(`Markera ${selected.length} plaggtyper som skickade till SIGN?`);
    if (!confirmed) return;

    setIsSending(true);
    try {
      await markOrdersAsSent(selected);
      setFeedback({ tone: "success", message: "Beställningarna har flyttats till historiken." });
      await loadOrders();
    } catch {
      setFeedback({ tone: "error", message: "Beställningarna kunde inte markeras som skickade." });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <BrandMark />
        <div>
          <span className="admin-pill">Administratör</span>
          <button className="quiet-button" type="button" onClick={() => void signOut()}>
            <LogOut size={18} />
            Logga ut
          </button>
        </div>
      </header>

      <div className="admin-content">
        <section className="admin-hero">
          <div>
            <span className="eyebrow">Orderöversikt</span>
            <h1>Klubbens beställningar</h1>
            <p>Samla, exportera och arkivera plaggen inför nästa beställning hos SIGN.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => void loadOrders()} disabled={isLoading}>
            <RefreshCw className={isLoading ? "spin" : ""} size={18} />
            Uppdatera
          </button>
        </section>

        <section className="admin-stats" aria-label="Ordersammanfattning">
          <article>
            <PackageOpen size={22} />
            <div><strong>{Object.keys(currentOrders).length}</strong><span>plaggtyper</span></div>
          </article>
          <article>
            <CheckCircle2 size={22} />
            <div><strong>{totalItems}</strong><span>plagg väntar</span></div>
          </article>
          <article>
            <Archive size={22} />
            <div><strong>{sentOrders.length}</strong><span>tidigare leveranser</span></div>
          </article>
        </section>

        <section className="orders-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Pågående</span>
              <h2>Aktuella beställningar</h2>
            </div>
            <span>{selected.length} markerade</span>
          </div>

          {isLoading ? (
            <div className="orders-loading"><span className="spinner" /><p>Hämtar beställningar…</p></div>
          ) : Object.keys(currentOrders).length === 0 ? (
            <div className="empty-state empty-state--orders">
              <PackageOpen size={32} />
              <h3>Inga aktuella beställningar</h3>
              <p>Nya beställningar visas här.</p>
            </div>
          ) : (
            <div className="order-groups">
              {Object.entries(currentOrders).map(([name, orders]) => (
                <OrderGroup
                  key={name}
                  name={name}
                  orders={orders}
                  selectable
                  selected={selected.includes(name)}
                  onSelectedChange={(isSelected) => toggleSelected(name, isSelected)}
                />
              ))}
            </div>
          )}

          <div className="admin-actions">
            <button className="secondary-button" type="button" disabled={selected.length === 0} onClick={() => downloadOrderCsv(currentOrders, selected)}>
              <Download size={18} />
              Exportera CSV
            </button>
            <button className="primary-button" type="button" disabled={selected.length === 0 || isSending} onClick={() => void handleMarkAsSent()}>
              <CheckCircle2 size={18} />
              {isSending ? "Sparar…" : "Markera som skickat"}
            </button>
          </div>
        </section>

        {sentOrders.length > 0 && (
          <section className="orders-section orders-section--history">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Historik</span>
                <h2>Skickat till SIGN</h2>
              </div>
            </div>
            <div className="delivery-list">
              {sentOrders.map((delivery, index) => (
                <details className="delivery" key={`${delivery.date}-${index}`}>
                  <summary>
                    <span><Archive size={18} /></span>
                    <strong>{dateFormatter.format(delivery.date)}</strong>
                    <small>{Object.keys(delivery.garments).length} plaggtyper</small>
                  </summary>
                  <div className="delivery__content">
                    {Object.entries(delivery.garments).map(([name, orders]) => (
                      <OrderGroup key={name} name={name} orders={orders} />
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>

      {feedback && <FeedbackToast feedback={feedback} onClose={() => setFeedback(null)} />}
    </main>
  );
}
